import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, SilentRequest } from '@azure/msal-browser';
import { EntraService } from './entra.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly entraService = inject(EntraService);
  private readonly msalService = inject(MsalService);

  get accountId() {
    return this.msalService.instance.getActiveAccount()?.localAccountId;
  }

  /**
   * Forces a silent token refresh so updated Entra role/group claims are picked up without
   * requiring the user to log out and back in. Also clears the cached groups lookup so the
   * next `getUserGroups()` call re-fetches from Entra rather than serving a stale result.
   *
   * Callers that poll this on a timer/loop are responsible for their own throttling interval.
   *
   * Returns the refreshed account, or `null` if there's no signed-in user or the refresh fails.
   */
  async forceRefresh(): Promise<AccountInfo | null> {
    const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
    if (!account) return null;

    const request: SilentRequest = {
      account,
      scopes: ['user.read'],
      forceRefresh: true
    };

    try {
      const response = await this.msalService.instance.acquireTokenSilent(request);
      if (response.account) {
        this.msalService.instance.setActiveAccount(response.account);
      }

      const oid = account.idTokenClaims?.['oid'];
      if (oid) {
        sessionStorage.removeItem(`userGroups:${oid}`);
      }

      return response.account ?? null;
    } catch (error) {
      console.error('Force token refresh failed:', error);
      return null;
    }
  }

  async getUserGroups(): Promise<string[]> {
    const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
    if (!account) return [];
    const oid = account.idTokenClaims?.['oid'];
    if (!oid) return [];

    const cacheKey = `userGroups:${oid}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try { return JSON.parse(cached); } catch { sessionStorage.removeItem(cacheKey); }
    }

    const groups = await this.entraService.getEntraUserGroups();
    sessionStorage.setItem(cacheKey, JSON.stringify(groups));
    return groups;
  }


  getUserRoles(): string[] {
    const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      // No user is signed in, return an empty array
      return [];
    }

    return account.idTokenClaims && account.idTokenClaims['roles'] ? (account.idTokenClaims['roles'] as string[]) : [];
  }

  async userHasGroup(group: string): Promise<boolean> {
    const groups = await this.getUserGroups();
    return groups.includes(group);
  }

  async userHasRole(role: string): Promise<boolean> {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  userHasAnyRole(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }
}
